/* eslint-disable prettier/prettier */
import JwtService from "@/core/services/jwt.service";
import { http } from "@/configurations/axios";
import { priceOptions, quantityOptions } from '@/modules/Application'
import { adminPermissionStore } from "@/adminPermissionStore";
import { store as StoreVenda } from '@/modules/Vendas/storeVenda.js'
import { store as storeNfe } from '@/modules/NFe/storeNfe.js'
import { store as storePedidos } from '@/modules/Pedidos/storePedidos.js'
import { store as StoreOrcamento } from '@/modules/Orcamento/storeOrcamento.js'
import { store as StoreOS } from '@/modules/OS/storeOS.js'
import { store as StoreServicos } from '@/modules/Servicos/storeServicos.js'
import { clienteInadimplenciaStore } from "@/modules/Application/stores/ClienteInadimplenciaStore";
import { setUserAccess, userAccessStore } from "@/userAccessStore";

const firstDefined = (...values) =>
  values.find((value) => value !== undefined && value !== null);

const toBoolean = (value) =>
  value === true || value === 1 || value === "1" || value === "true";

const backendBoolean = (...values) => {
  const value = firstDefined(...values);
  return value === undefined ? undefined : toBoolean(value);
};

const normalizeRevendaId = (value) => {
  if (value === undefined || value === null || value === "") return null;

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : value;
};

const buildAccountFlags = (user = {}, fallback = {}) => {
  const empresa = user?.empresa || {};
  const dadosRevenda = empresa?.dados_revenda || {};
  const idRevenda = normalizeRevendaId(firstDefined(
    dadosRevenda?.id_revenda,
    empresa?.id_revenda,
    user?.id_revenda,
    fallback.idRevenda,
    null
  ));
  const isNuts =
    backendBoolean(
      empresa?.isNuts,
      empresa?.is_nuts,
      user?.isNuts,
      user?.is_nuts
    ) ?? false;
  const isRevenda =
    backendBoolean(
      empresa?.isRevenda,
      empresa?.is_revenda,
      empresa?.revenda,
      user?.isRevenda,
      user?.is_revenda,
      user?.revenda
    ) ?? false;
  const isClienteRevenda =
    backendBoolean(
      empresa?.isClienteRevenda,
      empresa?.is_cliente_revenda,
      user?.isClienteRevenda,
      user?.is_cliente_revenda
    ) ?? (!isNuts && !isRevenda && !!idRevenda && idRevenda !== 1);
  const isClienteNutsDireto =
    backendBoolean(
      empresa?.isClienteNutsDireto,
      empresa?.is_cliente_nuts_direto,
      user?.isClienteNutsDireto,
      user?.is_cliente_nuts_direto
    ) ?? (!isNuts && !isRevenda && idRevenda === 1);

  return {
    idRevenda,
    isNuts,
    isRevenda,
    isClienteRevenda,
    isClienteNutsDireto,
  };
};

// action types
export const VERIFY_AUTH = "verifyAuth";
export const LOGIN = "login";
export const LOGOUT = "logout";
export const REGISTER = "register";
export const VERIFY_ASSINATURA = "verifyAssinatura";

// mutation types
export const PURGE_AUTH = "logOut";
export const SET_AUTH = "setUser";
export const SET_ERROR = "setError";
export const SET_ASSINATURA = "setAssinatura";

const state = {
  errors: null,
  user: {},
  isAuthenticated: !!JwtService.getToken(),
  itsblocked: false,
  isNuts: false,
  isRevenda: false,
  isClienteRevenda: false,
  isClienteNutsDireto: false,
  idRevenda: null,
  dadosRevenda: {},
  nutsDigital: false,
  licenca: null,
  vendedor: {},
  empresa: {
    features: [],
  },
};

const getters = {
  currentUser(state) {
    return state.user;
  },
  isAuthenticated(state) {
    return state.isAuthenticated;
  },
  itsblocked(state) {
    return state.itsblocked;
  },
  isNuts(state) {
    return state.isNuts;
  },
  isRevenda(state) {
    return state.isRevenda;
  },
  isClienteRevenda(state) {
    return state.isClienteRevenda;
  },
  isClienteNutsDireto(state) {
    return state.isClienteNutsDireto;
  },
  idRevenda(state) {
    return state.idRevenda;
  },
  dadosRevenda(state) {
    return state.dadosRevenda;
  },
  nutsDigital(state) {
    return state.nutsDigital;
  },
  licenca(state) {
    return state.licenca;
  },
  vendedor(state) {
    return state.vendedor;
  },
  empresa(state) {
    return state.empresa;
  },
  features(state) {
    return state.empresa?.features || [];
  },
  hasFeature: (state) => (code) => {
    return (state.empresa?.features || []).includes(code);
  }
};

const actions = {
  [LOGIN](context, credentials) {
    return new Promise((resolve, reject) => {
      http
        .post("/autenticacao", credentials)
        .then(({ data }) => {
          context.commit(SET_AUTH, data);
          resolve(data);
        })
        .catch(({ response }) => {
          reject(response);
        });
    });
  },
  [LOGOUT](context) {
    return new Promise((resolve, reject) => {
      http
        .post("/autenticacao/logout")
        .then(async ({ data }) => {
          context.commit(PURGE_AUTH);
          resolve(data);
        })
        .catch(({ response }) => {
          reject(response);
        });
    });
  },
  [REGISTER](context, credentials) {
    return new Promise((resolve, reject) => {
      http
        .post("/empresa/create", credentials)
        .then(async ({ data }) => {
          await context.dispatch(LOGIN, {
            email: credentials.email,
            password: credentials.password
          });
          resolve(data);
        })
        .catch(({ response }) => {
          reject(response);
        });
    });
  },
  [VERIFY_AUTH](context) {
    if (JwtService.getToken()) {
      return http
        .get("/autenticacao/dados")
        .then(({ data }) => {
          quantityOptions.precision = data?.configuracoes?.casas_decimal_quantidade;
          priceOptions.precision = data?.configuracoes?.casas_decimal_preco;
          StoreVenda.aberturaCaixa = data?.configuracoes?.abertura_caixa ?? false;
          StoreVenda.fechamentoAsCegas = data?.configuracoes?.fechamento_as_cegas ?? false;
          adminPermissionStore.requestPermission = data?.configuracoes?.solicita_senha_adm ?? false;
          StoreVenda.verificaEstoque = data?.configuracoes?.verifica_estoque ?? false;
          storeNfe.verificaEstoque = data?.configuracoes?.verifica_estoque ?? false;
          storePedidos.verificaEstoque = data?.configuracoes?.verifica_estoque ?? false;
          StoreVenda.naoGeraImpressao = data?.configuracoes?.nao_gerar_impressao ?? false;
          StoreVenda.vendedorObrigatorio = data?.configuracoes?.vendedor_obrigatorio ?? false;
          storePedidos.pafNFCe = data?.paf_nfce ?? false;
          StoreVenda.pafNFCe = data?.paf_nfce ?? false;
          StoreOrcamento.pafNFCe = data?.paf_nfce ?? false;
          StoreOS.pafNFCe = data?.paf_nfce ?? false;
          StoreServicos.informacoesComplementares = data?.configuracoes.informacao_nfse ?? null;
          StoreOS.trabalharHorasOs = data?.configuracoes?.trabalhar_horas_os ?? false;
          StoreOS.trabalharNumeroInternoOs = data?.configuracoes?.trabalhar_numero_interno_os ?? false;
          storeNfe.importarOBSProduto = data?.configuracoes?.importar_obs_produto ?? false;
          clienteInadimplenciaStore.bloquearVendaClienteInadimplente = data?.configuracoes?.bloquear_venda_cliente_inadimplente ?? false;
          clienteInadimplenciaStore.limiteInadimplenciaPermitida = data?.configuracoes?.limite_inadimplencia_permitida ?? 0;
          if (data?.admin) adminPermissionStore.requestPermission = false;
          context.commit(SET_AUTH, data);
        })
        .catch(({ response }) => {
          context.commit(SET_ERROR, response?.data?.errors || response?.data);
        });
    } else {
      context.commit(PURGE_AUTH);
      return Promise.resolve(); // <- importantíssimo para poder dar await
    }
  },
  [VERIFY_ASSINATURA](context) {
    if (JwtService.getToken()) {
      return http
        .post("/empresa/assinatura")
        .then(({ data }) => {
          if (data) {
            context.commit(SET_ASSINATURA, data);
          }
        })
        .catch(({ response }) => {
          context.commit(SET_ERROR, response?.data?.errors || response?.data);
        });
    } else {
      context.commit(PURGE_AUTH);
      return Promise.resolve();
    }
  }
};

const mutations = {
  [SET_ERROR](state, error) {
    state.errors = error;
  },
  [SET_AUTH](state, user) {
    user = user || {};
    const empresa = user?.empresa || {};
    const assinatura = empresa?.assinatura || {};
    const dadosRevenda = empresa?.dados_revenda || {};
    const accountFlags = buildAccountFlags(user, state);

    state.isAuthenticated = true;
    state.errors = {};
    state.user = user;
    state.empresa = {
      ...empresa,
      features: Array.isArray(empresa?.features) ? empresa.features : [],
    };
    setUserAccess(user?.recursos || []);
    state.itsblocked = assinatura?.bloqueado ?? false;
    if (user.token) {
      JwtService.saveToken(state.user.token);
    }
    state.isNuts = accountFlags.isNuts;
    state.isRevenda = accountFlags.isRevenda;
    state.isClienteRevenda = accountFlags.isClienteRevenda;
    state.isClienteNutsDireto = accountFlags.isClienteNutsDireto;
    if (state.isRevenda) {
      JwtService.saveParceiro(state.isRevenda);
    }
    state.idRevenda = accountFlags.idRevenda;
    state.dadosRevenda = dadosRevenda;
    state.nutsDigital = user?.nuts_digital ?? false;
    state.licenca = user;
    state.vendedor = user;
  },
  [PURGE_AUTH](state) {
    state.isAuthenticated = false;
    state.user = {};
    state.errors = {};
    JwtService.destroyToken();
    JwtService.destroyTokenCliente();
    JwtService.destroyParceiro();;
    state.itsblocked = false;
    state.isNuts = false;
    state.isRevenda = false;
    state.isClienteRevenda = false;
    state.isClienteNutsDireto = false;
    state.idRevenda = null;
    state.dadosRevenda = {};
    state.nutsDigital = null;
    state.licenca = null;
    state.vendedor = {};
    state.empresa = { features: [] };
    userAccessStore.access = {};
    userAccessStore.disabled = {};
    userAccessStore.tooltips = {};
  },
  [SET_ASSINATURA](state, data) {
    state.itsblocked = data.bloqueado;
    state.isNuts = backendBoolean(data.nuts, data.isNuts, data.is_nuts) ?? state.isNuts;
    state.isRevenda =
      backendBoolean(data.revenda, data.isRevenda, data.is_revenda) ??
      state.isRevenda;
    state.idRevenda = normalizeRevendaId(
      firstDefined(data.id_revenda, data.idRevenda, state.idRevenda)
    );
    state.isClienteRevenda =
      backendBoolean(
        data.isClienteRevenda,
        data.is_cliente_revenda,
        data.cliente_revenda
      ) ??
      (!state.isNuts &&
        !state.isRevenda &&
        !!state.idRevenda &&
        state.idRevenda !== 1);
    state.isClienteNutsDireto =
      backendBoolean(
        data.isClienteNutsDireto,
        data.is_cliente_nuts_direto,
        data.cliente_nuts_direto
      ) ??
      (!state.isNuts && !state.isRevenda && state.idRevenda === 1);
    state.dadosRevenda.id_revenda = state.idRevenda;
    state.dadosRevenda.nome_revenda =
      data.nome_revenda ?? state.dadosRevenda.nome_revenda;
    state.dadosRevenda.fone_revenda =
      data.fone_revenda ?? state.dadosRevenda.fone_revenda;
    state.nutsDigital = data.nuts_digital ?? state.nutsDigital;
    state.licenca = data;
  }
};

export default {
  state,
  actions,
  mutations,
  getters,
};
